import type {
  GridApiPremium,
  GridRowId,
  GridTreeNode,
} from '@mui/x-data-grid-premium';
import type { AccountBase } from '@/models/AccountViewModel';

/**
 * Field of the hidden column the balance sheet drill-downs group on. It holds
 * the second tier of Account Type, which is what legacy grouped by.
 */
export const SUBCATEGORY_FIELD = 'subcategory';

/** Sums a grouped column while grouping is on. */
export const BALANCE_SUM_AGGREGATION_MODEL = { Balance: 'sum' };

/**
 * The account's subcategory — the second tier of its Account Type — or null.
 *
 * The classification join that produces it is a left join, and some accounts are
 * only classified two levels deep, so it is routinely absent. The API sends the
 * literal "Not set" for those as well as an empty value.
 */
export function subcategoryOf(account: AccountBase): string | null {
  const name = account.SubClassificationName?.trim();
  if (!name || name.toLowerCase() === 'not set') {
    return null;
  }
  return name;
}

/**
 * Whether a set of accounts has any subcategory to group by.
 *
 * Several level-2 categories carry none at all — Checking, Savings, CD, Money
 * Market, Installment Loan and Line Of Credit among them — and grouping those
 * puts every account in a single "Uncategorized" group wrapping the whole list,
 * which says less than the plain list does.
 */
export function hasSubcategories(accounts: AccountBase[]): boolean {
  return accounts.some((account) => subcategoryOf(account) !== null);
}

/**
 * Whether a rendered row is a real account rather than a group header or an
 * aggregation footer the grid generated. Cells that read account fields or open
 * account UI have to skip the generated rows, which carry none of those fields.
 */
export function isAccountRow(rowNode: GridTreeNode): boolean {
  return rowNode.type === 'leaf';
}

/** How many accounts a group holds and what they come to. */
export interface GroupSummary {
  count: number;
  total: number;
}

/**
 * Summarises a group from the accounts the user can actually see, so the count
 * and the subtotal always describe the same rows once a filter is active.
 *
 * Mobile draws its own group header, so it has to produce what the grid
 * aggregates into the desktop group row itself. The grid keeps aggregated values
 * in its own state keyed by row id rather than on the group node.
 */
export function summarizeGroup(
  api: GridApiPremium,
  groupId: GridRowId,
): GroupSummary {
  const ids = api.getRowGroupChildren({ groupId, applyFiltering: true });
  const total = ids.reduce<number>(
    (sum, id) => sum + (api.getRow<AccountBase>(id)?.Balance ?? 0),
    0,
  );
  return { count: ids.length, total };
}
